import React from 'react'
import { Link } from 'react-router-dom'
import { pricingPackages, companyInfo } from '../data'
import SubpageHero from '../components/SubpageHero'
import useMeta from '../hooks/useMeta'

export default function PricesPage() {
  useMeta('Priser', 'Gennemskuelige priser på privat rengøring, housekeeping og erhvervsrengøring. Se vores faste timepriser og indhent et uforpligtende tilbud.')
  const heroImages = [
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/scandinavian-luxury-living-room-clean.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/kitchen-cleaning-service.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/danish-modern-house-exterior.jpg'
  ]

  return (
    <div className="flex flex-col min-h-screen bg-brand-light">
      {/* Hero Section */}
      <SubpageHero
        title="Gennemskuelige priser uden skjulte gebyrer"
        intro="Vi tilbyder faste, konkurrencedygtige priser på vores standardydelser og skræddersyede tilbud på større opgaver."
        images={heroImages}
      />

      {/* Pricing Packages Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
            {pricingPackages.map((pkg, index) => (
              <div
                key={index}
                className={`border p-8 flex flex-col justify-between relative bg-white transition-all duration-200 ${
                  pkg.popular
                    ? 'border-black shadow-lg ring-1 ring-black/30 scale-105 z-10'
                    : 'border-brand-border shadow-sm hover:shadow-md'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-black px-4 py-1 text-xs font-bold text-white uppercase tracking-wider">
                    Mest populær
                  </span>
                )}

                <div className="text-left">
                  <h3 className="text-xl font-bold text-brand-navy">{pkg.name}</h3>
                  <p className="mt-2 text-sm text-brand-text-muted leading-relaxed">{pkg.description}</p>
                  
                  {/* Price */}
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-3xl md:text-4xl font-extrabold text-brand-navy tracking-tight">{pkg.price}</span>
                    <span className="text-xs text-brand-text-muted font-medium ml-1">{pkg.period}</span>
                  </div>

                  <div className="my-6 border-t border-brand-border" />

                  {/* Features */}
                  <ul className="space-y-3 text-sm text-brand-text-muted">
                    {pkg.features.map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="fa-solid fa-check text-black mt-0.5 shrink-0"></i>
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8">
                  <Link
                    to="/kontakt"
                    className={`block w-full py-3 text-center text-sm font-bold transition-all duration-200 ${
                      pkg.popular
                        ? 'bg-brand-navy text-white hover:bg-brand-darkblue shadow-md hover:shadow-lg'
                        : 'bg-brand-gray text-brand-navy hover:bg-brand-border border border-brand-border'
                    }`}
                  >
                    {pkg.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Standard Info Text */}
          <div className="mt-16 max-w-6xl mx-auto text-left border-t border-brand-border pt-8">
            <h4 className="text-lg font-bold text-brand-navy flex items-center gap-2.5">
              Værd at vide om vores priser
            </h4>
            <div className="mt-4 space-y-3 text-sm text-brand-text-muted leading-relaxed">
              <p>
                <strong>Inkluderet i prisen:</strong> Alle priser for privat rengøring er angivet inkl. 25% moms, forsikring, kørsel og professionelle, svanemærkede rengøringsmidler samt udstyr. Der kommer ingen uventede tillæg.
              </p>
              <p>
                <strong>Servicefradrag (Håndværkerfradrag):</strong> Husk, at du som privatperson kan trække udgiften til rengøringshjælp fra i skat via servicefradraget. Det betyder, at den reelle nettoudgift er væsentligt lavere.
              </p>
              <p>
                <strong>Aflysningspolitik:</strong> Planlagte rengøringer kan aflyses eller rykkes omkostningsfrit op til 24 timer før aftalens start.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-navy text-white py-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(0,102,255,0.15),transparent_50%)]" />
        <div className="mx-auto max-w-4xl px-6 relative z-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Klar til at bestille eller ønsker du et skræddersyet tilbud?</h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Udfyld vores kontaktformular, eller ring til os direkte. Vi vender hurtigt tilbage med et uforpligtende tilbud tilpasset dine ønsker.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-white text-brand-navy px-6 py-3.5 text-sm font-bold transition-all duration-200"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
              className="bg-transparent px-6 py-3.5 text-sm font-bold text-white border border-white transition-all duration-200 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i> {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
